import { ref, readonly } from 'vue'

/**
 * Singleton WebSocket for the chat system.
 *
 * Lifecycle:
 *  - Mints a ticket via POST /api/chat/ws-ticket, then connects to /_ws/chat?ticket=…
 *  - Jittered exponential backoff on reconnect (1s → 30s cap) — retries forever.
 *    Drops back to a "slow retry" every 30s once the exponential climb hits the
 *    cap so dead networks don't burn battery but also never give up.
 *  - Server sends `ping` every 25s; we consider the connection dead after 45s silence
 *  - Outgoing sends are queued while disconnected and flushed on reconnect
 *  - Pub/sub API: `on(type, handler) → off()` for server-pushed frames
 *
 * Gracefully handles unavailable chat (tenant.chatEnabled=false ⇒ ticket 404):
 * the socket stays in status 'offline' and never retries until reload.
 */

type SocketStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'offline'
type FrameHandler = (frame: any) => void

interface OutboundMessage { data: string; queuedAt: number }

let ws: WebSocket | null = null
let heartbeatTimer: ReturnType<typeof setTimeout> | null = null
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let reconnectAttempt = 0
let stopped = false
let disabled = false
const outbox: OutboundMessage[] = []
const handlers = new Map<string, Set<FrameHandler>>()

const statusRef = ref<SocketStatus>('idle')
const lastErrorRef = ref<string | null>(null)
const RECONNECT_CAP_MS = 30_000
/** Max queued outbound frames while disconnected — guards against OOM if the
 *  socket stays down while the UI keeps pumping events. Oldest gets dropped. */
const OUTBOX_CAP = 100

function clearHeartbeat() {
    if (heartbeatTimer) { clearTimeout(heartbeatTimer); heartbeatTimer = null }
}
function clearReconnect() {
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null }
}

function scheduleHeartbeatWatchdog() {
    clearHeartbeat()
    heartbeatTimer = setTimeout(() => {
        // No server ping in >45s → consider connection dead
        try { ws?.close(4000, 'heartbeat_timeout') } catch { /* noop */ }
    }, 45_000)
}

function emit(type: string, frame: any) {
    const set = handlers.get(type)
    if (!set) return
    for (const h of set) {
        try { h(frame) } catch (err) { console.error('[chat:ws] handler threw for', type, err) }
    }
}

async function mintTicket(): Promise<string> {
    const res = await $fetch<{ ticket: string }>('/api/chat/ws-ticket', { method: 'POST' })
    return res.ticket
}

function scheduleReconnect() {
    if (stopped || disabled) return
    // Infinite retries — the chat must keep trying. Exponential up to a
    // 30s cap, then steady 30s±jitter forever until either the network
    // comes back or the user logs out / navigates away.
    const base = Math.min(RECONNECT_CAP_MS, 1000 * 2 ** reconnectAttempt)
    const jitter = Math.floor(Math.random() * 1000)
    const delay = base + jitter
    reconnectAttempt += 1
    statusRef.value = 'reconnecting'
    clearReconnect()
    reconnectTimer = setTimeout(() => connect(), delay)
}

async function connect() {
    if (typeof window === 'undefined') return
    // `disabled` is sticky (chat is off for this tenant — don't re-mint tickets
    // that can only 404 again), but `stopped` is NOT: an explicit connect() is
    // a caller stating it wants a connection, which cancels an earlier stop().
    // ChatPanel calls stop() on unmount and connect() on mount, so any route
    // that hides the panel (e.g. the /onboarding wizard) would otherwise leave
    // `stopped` latched and the socket dead until a full page reload.
    if (disabled) return
    stopped = false
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return

    statusRef.value = 'connecting'

    let ticket: string
    try {
        ticket = await mintTicket()
    } catch (err: any) {
        // 404 = feature flag off or tenant not resolved → stop retrying
        if (err?.statusCode === 404) {
            disabled = true
            statusRef.value = 'offline'
            lastErrorRef.value = 'chat_not_available'
            return
        }
        lastErrorRef.value = err?.statusMessage || err?.message || 'ticket_failed'
        scheduleReconnect()
        return
    }

    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
    const url = `${proto}://${window.location.host}/_ws/chat?ticket=${encodeURIComponent(ticket)}`

    let sock: WebSocket
    try {
        sock = new WebSocket(url)
    } catch (err: any) {
        lastErrorRef.value = err?.message || 'ws_construct_failed'
        scheduleReconnect()
        return
    }
    ws = sock

    // Every handler below is bound to THIS socket instance and bails if it is
    // no longer the current one. A stop()/connect() pair fired in quick
    // succession (panel unmount → remount on a route change) leaves the old
    // socket's close event in flight; without this guard its handler would
    // null out the freshly created `ws` and emit a bogus '__close'.
    const isCurrent = () => ws === sock

    sock.onopen = () => {
        if (!isCurrent()) return
        statusRef.value = 'open'
        lastErrorRef.value = null
        reconnectAttempt = 0
        scheduleHeartbeatWatchdog()
        // Flush queued messages
        while (outbox.length > 0) {
            const msg = outbox.shift()!
            try { sock.send(msg.data) } catch { /* re-queue not worth it */ }
        }
        emit('__open', {})
    }

    sock.onmessage = (ev) => {
        if (!isCurrent()) return
        scheduleHeartbeatWatchdog()
        let frame: any
        try { frame = JSON.parse(typeof ev.data === 'string' ? ev.data : '') } catch { return }
        if (!frame || typeof frame !== 'object') return

        if (frame.type === 'ping') {
            // Reply
            try { sock.send(JSON.stringify({ type: 'pong' })) } catch { /* noop */ }
            return
        }

        emit(frame.type, frame)
        emit('*', frame)
    }

    sock.onerror = () => {
        if (!isCurrent()) return
        lastErrorRef.value = 'ws_error'
    }

    sock.onclose = () => {
        if (!isCurrent()) return
        clearHeartbeat()
        ws = null
        if (!stopped && !disabled) scheduleReconnect()
        else statusRef.value = 'offline'
        emit('__close', {})
    }
}

function stop() {
    stopped = true
    clearHeartbeat()
    clearReconnect()
    if (ws) {
        try { ws.close(1000, 'client_stop') } catch { /* noop */ }
        ws = null
    }
    statusRef.value = 'idle'
}

function resume() {
    if (!stopped && !disabled) return
    stopped = false
    disabled = false
    reconnectAttempt = 0
    connect()
}

/**
 * Kick a reconnect attempt RIGHT NOW, skipping the scheduled backoff.
 * Called on network-online and tab-visible events so we don't sit idle
 * for 30s after the laptop wakes up or Wi-Fi recovers.
 */
function reconnectNow() {
    if (stopped || disabled) return
    if (ws && ws.readyState === WebSocket.OPEN) return
    clearReconnect()
    reconnectAttempt = 0
    connect()
}

// Browser-level signals that the network might be usable again.
// Safe to wire up at module load — there's only one socket singleton.
if (typeof window !== 'undefined') {
    window.addEventListener('online', () => { reconnectNow() })
    window.addEventListener('focus', () => { reconnectNow() })
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') reconnectNow()
    })

    // Close the socket explicitly when the tab is going away so the server
    // sees a clean close-frame and marks presence offline immediately. Without
    // this, the server has to wait up to ~45s for the heartbeat watchdog to
    // detect a dead connection — during which other users still see this user
    // as online. `pagehide` covers mobile Safari + bfcache eviction (where
    // `beforeunload` is often suppressed); `beforeunload` covers desktop
    // tab/window close. We don't flip `stopped` so a bfcache restore lets the
    // existing focus/visibility listeners reconnect.
    const closeOnUnload = () => {
        if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
            try { ws.close(1000, 'unload') } catch { /* noop */ }
        }
    }
    window.addEventListener('pagehide', closeOnUnload)
    window.addEventListener('beforeunload', closeOnUnload)
}

function send(frame: any): boolean {
    const data = JSON.stringify(frame)
    if (ws && ws.readyState === WebSocket.OPEN) {
        try { ws.send(data); return true } catch { /* fall through to queue */ }
    }
    if (outbox.length >= OUTBOX_CAP) {
        outbox.shift()
        console.warn('[chat] outbox full — dropped oldest')
    }
    outbox.push({ data, queuedAt: Date.now() })
    return false
}

function on(type: string, handler: FrameHandler): () => void {
    let set = handlers.get(type)
    if (!set) { set = new Set(); handlers.set(type, set) }
    set.add(handler)
    return () => set!.delete(handler)
}

export function useChatSocket() {
    return {
        status: readonly(statusRef),
        lastError: readonly(lastErrorRef),
        connect,
        stop,
        resume,
        send,
        on,
    }
}
